// cleanup_convert_to_typescript_file_SECURITY_CONTACT

import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { template } from 'lodash';

import { Button, PRIMARY, TERTIARY } from '@glu/buttons-react';
import { Input } from '@glu/form-components';

import PhoneNumber from 'components/phoneNumber/PhoneNumber';
import emailAddress from 'utils/validations/emailAddress';
import phoneNumber from 'utils/validations/phoneNumber';
import { SecurityContact } from 'proptypes/user';
import { sendActivationCode } from 'api/user/service';
import useLocale from 'components/useLocale';

import useStyles from './SecurityContactField.styles';
import {
  contactTypes,
  INPUT_TYPES,
  SMS_NUMBER_MAX,
  VOICE_CALL_NUMBER_FORMAT
} from './securityContacts.constants';
import reducer, { ACTIONS } from './SecurityContactField.reducer';

const initialState = {
  activationCode: '',
  sending: false
};

const { CANCEL, HANDLE_INPUT_CHANGE, SET_SENDING } = ACTIONS;

const SecurityFieldComponent = ({
  contact,
  contact: {
    contactType,
    errorMessage,
    extension = '',
    inputType,
    name,
    sent,
    value = '',
    verified,
    vruDelay
  },
  disabled,
  format,
  handleError: handleErrorFromProps,
  onChange,
  onReset,
  resetError
}) => {
  const locale = useLocale();
  const { btnLink, inputsGroup } = useStyles();
  const [{ numberText, sending }, dispatch] = useReducer(reducer, {
    ...initialState,
    numberText: contact?.value || ''
  });
  const staticValue = value.replace(/(\d{3})(\d{3})(\d{4})/i, format);
  const showActivation =
    contactType === contactTypes.sms &&
    !errorMessage &&
    value &&
    !verified &&
    !sent &&
    value.length >= 10;
  const maxLength = contactType === contactTypes.sms ? SMS_NUMBER_MAX : '';

  const change = (changeValue) => {
    onChange({ ...contact, ...changeValue });
  };

  const handleInputChange = ({ target = {}, number, value: numberText, extension, vruDelay }) => {
    const value = number || target.value || '';

    const changeValue = {
      extension,
      numberText,
      sent: false,
      value: value.replace(/\s/g, ''),
      verified: false,
      vruDelay
    };

    change(changeValue);
    dispatch({ type: HANDLE_INPUT_CHANGE, payload: changeValue });
  };

  const validations = () => {
    if (inputType === INPUT_TYPES.PHONE) {
      return change({
        errorMessage:
          value &&
          phoneNumber(value, { locale, skipLengthValidation: contactType === contactTypes.voice })
      });
    }
    if (inputType === INPUT_TYPES.EMAIL) {
      return change({ errorMessage: value && emailAddress(value, { locale }) });
    }
    return null;
  };

  const remove = () => {
    handleInputChange({ extension: '', number: '', value: '', vruDelay: '' });
  };

  const cancel = () => {
    dispatch({
      type: CANCEL,
      payload: {
        errorMesage: '',
        numberText: '',
        successMessage: ''
      }
    });

    return onReset(contact);
  };

  const handleError = (data) => {
    handleErrorFromProps(data?.data);
  };

  const handleResponse = (data) => {
    const { success: sent } = data;
    dispatch({ type: SET_SENDING, payload: false });
    if (!sent) {
      return handleError(data);
    }
    return change({ errorMessage: '', sent });
  };

  const sendCode = () => {
    resetError();
    dispatch({ type: SET_SENDING, payload: true });

    sendActivationCode({
      contactType,
      default: false,
      value
    })
      .then(handleResponse)
      .catch(handleError);
  };

  if (verified && value) {
    return (
      <p data-qa={`${contactType}-saved-value`}>
        {staticValue}
        {extension ? `, ${extension}` : ''}
        <br />
        {vruDelay
          ? `${locale['common.delay'].toLowerCase()} : ${template(
              locale['common.time.template.seconds']
            )({ seconds: vruDelay })}`
          : ''}
        <br />
        <Button
          variant={TERTIARY}
          className="btn btn-link btn-tertiary"
          dataQa={`${contactType}-remove`}
          type="button"
          disabled={disabled}
          onClick={remove}
          text={locale['common.remove']}
        />
      </p>
    );
  }

  return (
    <div className={classnames({ [inputsGroup]: showActivation })}>
      {inputType === INPUT_TYPES.PHONE && (
        <PhoneNumber
          autoComplete="off"
          disabled={disabled}
          format={contactType === contactTypes.sms ? format : VOICE_CALL_NUMBER_FORMAT}
          id={name}
          onChange={handleInputChange}
          onBlur={validations}
          name={name}
          value={value && numberText}
          data-qa={`${contactType}-input`}
          maxLength={maxLength}
        />
      )}
      {inputType === INPUT_TYPES.EMAIL && (
        <Input
          data-qa={`${contactType}-input`}
          autoComplete="off"
          disabled={disabled}
          id={name}
          onChange={handleInputChange}
          onBlur={validations}
          name={name}
          maxLength="255"
          type="text"
          value={value}
        />
      )}

      {showActivation && (
        <>
          <Button
            variant={PRIMARY}
            className="btn btn-primary"
            onClick={sendCode}
            disabled={disabled || sending}
            dataQa="send-activation-code-button"
            text={locale['profile.activationCode']}
          />
          <Button
            variant={TERTIARY}
            className={classnames('btn', 'btn-link', btnLink)}
            disabled={disabled}
            onClick={cancel}
            text={locale['common.cancel']}
          />
        </>
      )}
    </div>
  );
};

SecurityFieldComponent.propTypes = {
  contact: SecurityContact,
  disabled: PropTypes.bool,
  format: PropTypes.string,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  resetError: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired
};

SecurityFieldComponent.defaultProps = {
  contact: {},
  disabled: false,
  format: '($1) $2-$3',
  onChange() {},
  onReset() {}
};

export default SecurityFieldComponent;
